const NEIGHBORS = [
  { dr: -1, dc: 0 },
  { dr: 0, dc: 1 },
  { dr: 1, dc: 0 },
  { dr: 0, dc: -1 }
]

class GearMergeGame {
  constructor(options = {}) {
    this.round = Math.max(1, Math.floor(options.round || 1))
    this.rows = Math.max(4, Math.min(7, Math.floor(options.rows || 5)))
    this.cols = Math.max(4, Math.min(7, Math.floor(options.cols || 5)))
    this.targetLevel = Math.max(
      4,
      Math.min(10, Math.floor(options.targetLevel || 6 + Math.floor((this.round - 1) / 2)))
    )
    this.seed = Number.isFinite(options.seed) ? Math.floor(options.seed) : Date.now()
    this.randomState = this.seed % 2147483647
    if (this.randomState <= 0) this.randomState += 2147483646
    this.board = Array.from({ length: this.rows }, () => Array(this.cols).fill(0))
    this.queue = []
    this.score = 0
    this.moves = 0
    this.highestLevel = 1
    this.status = 'playing'
    this.lastMerge = null
    this.createInitialBoard(Math.min(this.rows * this.cols - 4, 4 + this.round))
    this.fillQueue()
  }

  random() {
    this.randomState = (this.randomState * 16807) % 2147483647
    return (this.randomState - 1) / 2147483646
  }

  randomIndex(length) {
    return Math.floor(this.random() * length)
  }

  inBounds(r, c) {
    return r >= 0 && r < this.rows && c >= 0 && c < this.cols
  }

  emptyCells() {
    const cells = []
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        if (this.board[r][c] === 0) cells.push({ r, c })
      }
    }
    return cells
  }

  hasNeighborLevel(r, c, level) {
    return NEIGHBORS.some(({ dr, dc }) => {
      const nr = r + dr
      const nc = c + dc
      return this.inBounds(nr, nc) && this.board[nr][nc] === level
    })
  }

  createInitialBoard(count) {
    for (let placed = 0; placed < count; placed++) {
      const level = this.random() < 0.72 ? 1 : 2
      const empty = this.emptyCells()
      const preferred = empty.filter((cell) => !this.hasNeighborLevel(cell.r, cell.c, level))
      const candidates = preferred.length > 0 ? preferred : empty
      const cell = candidates[this.randomIndex(candidates.length)]
      this.board[cell.r][cell.c] = level
      this.highestLevel = Math.max(this.highestLevel, level)
    }
  }

  drawQueueLevel() {
    const roll = this.random()
    if (this.round >= 3 && roll > 0.95) return 3
    if (roll > 0.69) return 2
    return 1
  }

  fillQueue() {
    while (this.queue.length < 3) this.queue.push(this.drawQueueLevel())
  }

  connectedComponent(startR, startC, level) {
    const component = []
    const visited = new Set([`${startR},${startC}`])
    const queue = [{ r: startR, c: startC }]
    for (let index = 0; index < queue.length; index++) {
      const current = queue[index]
      component.push(current)
      for (const { dr, dc } of NEIGHBORS) {
        const r = current.r + dr
        const c = current.c + dc
        const key = `${r},${c}`
        if (!this.inBounds(r, c) || visited.has(key) || this.board[r][c] !== level) continue
        visited.add(key)
        queue.push({ r, c })
      }
    }
    return component
  }

  resolveMerges(anchorR, anchorC) {
    const cascades = []
    let chain = 0
    while (true) {
      const level = this.board[anchorR][anchorC]
      const component = this.connectedComponent(anchorR, anchorC, level)
      if (component.length < 2) break
      chain += 1
      for (const cell of component) this.board[cell.r][cell.c] = 0
      const newLevel = level + 1
      this.board[anchorR][anchorC] = newLevel
      this.highestLevel = Math.max(this.highestLevel, newLevel)
      const chainScore = component.length * 25 * 2 ** (newLevel - 1) * chain
      this.score += chainScore
      cascades.push({
        chain,
        level,
        newLevel,
        cells: component,
        anchor: { r: anchorR, c: anchorC },
        chainScore
      })
    }
    this.lastMerge = cascades[cascades.length - 1] || null
    return cascades
  }

  place(r, c) {
    if (this.status !== 'playing') return { ok: false, reason: 'not_playing', state: this.getState() }
    if (!this.inBounds(r, c)) return { ok: false, reason: 'out_of_bounds', state: this.getState() }
    if (this.board[r][c] !== 0) return { ok: false, reason: 'occupied', state: this.getState() }

    const placedLevel = this.queue.shift()
    this.board[r][c] = placedLevel
    this.highestLevel = Math.max(this.highestLevel, placedLevel)
    this.moves += 1
    const cascades = this.resolveMerges(r, c)
    this.fillQueue()

    if (this.highestLevel >= this.targetLevel) this.status = 'won'
    else if (this.emptyCells().length === 0) this.status = 'lost'

    return {
      ok: true,
      placed: { r, c, level: placedLevel },
      cascades,
      state: this.getState()
    }
  }

  getState() {
    return {
      round: this.round,
      rows: this.rows,
      cols: this.cols,
      targetLevel: this.targetLevel,
      score: this.score,
      moves: this.moves,
      highestLevel: this.highestLevel,
      status: this.status,
      emptyCells: this.emptyCells().length,
      currentLevel: this.queue[0],
      queue: [...this.queue],
      board: this.board.map((row) => [...row])
    }
  }
}

module.exports = { GearMergeGame, NEIGHBORS }
